import { Box, Typography, styled } from '@mui/material';
import { History as HistoryIcon, Warning as WarningIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTimetableRecordsDirect,
  getActiveByClassDirect,
  getWithAssignmentsDirect,
  getActiveDirect,
} from '@timetable/api';
import { queryKeys } from '../../lib/queryKeys';
import { useAuthStore } from '../../store/authStore';
import { useTimetableDesignerStore } from '../../store/timetableDesignerStore';
import { useSubstitutionOverlay } from '../../hooks/useSubstitutionOverlay';
import { LoadingSpinner } from '../LoadingSpinner';
import { useState, useEffect, useRef } from 'react';
import type { TimetableRecordWithRelations, SchedulePeriod, DayOfWeek } from '@timetable/types';
import { PeriodCard } from './PeriodCard';
import { BreakPeriodCard } from './BreakPeriodCard';
import { EmptySlotCell } from './EmptySlotCell';
import { StructuralChangeDialog } from './StructuralChangeDialog';

const DAY_ORDER: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const DAY_LABELS: Record<DayOfWeek, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
};

// ── Styled components (matching MultiSectionTimetableGrid) ───────────────────

const DayHeader = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 4,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(0.75, 1),
  borderRadius: 0,
  textAlign: 'center',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const TimeLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(0.25, 0.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 0,
  fontSize: '0.7rem',
  color: theme.palette.text.secondary,
}));

const PeriodCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFocused',
})<{ isFocused?: boolean }>(({ theme, isFocused }) => ({
  minHeight: '52px',
  display: 'flex',
  alignItems: 'stretch',
  outline: isFocused ? `3px solid ${theme.palette.primary.main}` : 'none',
  outlineOffset: '-3px',
  transition: 'outline 0.15s',
  cursor: 'pointer',
  '& > *': { flex: 1 },
  '&:focus': {
    outline: `3px solid ${theme.palette.primary.main}`,
    outlineOffset: '-3px',
  },
}));

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(time: string | null | undefined): string {
  if (!time) return '--:--';
  return time.substring(0, 5);
}

interface TimetableGridProps {
  entityId: string;
  viewMode: 'class' | 'teacher' | 'subject';
}

interface CellPosition {
  dayIndex: number;
  periodIndex: number;
}

export function TimetableGrid({ entityId, viewMode }: TimetableGridProps) {
  const { schoolContext } = useAuthStore();
  const { selectPeriod, temporalDate, activeAcademicYear, setActiveAcademicYear } = useTimetableDesignerStore();
  const queryClient = useQueryClient();
  const [focusedCell, setFocusedCell] = useState<CellPosition | null>(null);
  const [structuralChangeDialogOpen, setStructuralChangeDialogOpen] = useState(false);
  const [recordForStructuralChange, setRecordForStructuralChange] = useState<TimetableRecordWithRelations | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Substitution overlay hook (Requirements 17.1, 17.4, 17.5)
  const {
    hasSubstitution,
    getSubstitution,
    isTeacherCovering,
    isTeacherAbsent,
    getTeacherAbsences,
    isLoading: loadingSubstitutions,
  } = useSubstitutionOverlay(schoolContext, temporalDate);

  const { data: activeYear } = useQuery({
    queryKey: queryKeys.academicYears.active(schoolContext || ''),
    queryFn: () => getActiveDirect(schoolContext || ''),
    enabled: !!schoolContext,
  });

  useEffect(() => {
    if (activeYear) setActiveAcademicYear(activeYear);
  }, [activeYear, setActiveAcademicYear]);

  const currentAcademicYear = activeAcademicYear?.name || activeYear?.name || null;

  const filters: Record<string, string> = { school_id: schoolContext || '' };
  if (viewMode === 'class') filters.class_section_id = entityId;
  else if (viewMode === 'teacher') filters.teacher_id = entityId;
  else if (viewMode === 'subject') filters.subject_id = entityId;

  const { data: records, isLoading: loadingRecords } = useQuery({
    queryKey: temporalDate
      ? [...queryKeys.timetable.list(filters), 'temporal', temporalDate.toISOString()]
      : queryKeys.timetable.list(filters),
    queryFn: async () => {
      if (temporalDate && viewMode === 'class') {
        const dateStr = temporalDate.toISOString().split('T')[0];
        return getWithAssignmentsDirect(entityId, dateStr, currentAcademicYear ?? '');
      }
      if (!temporalDate && viewMode === 'class') {
        return getActiveByClassDirect(entityId, currentAcademicYear ?? '');
      }
      return getTimetableRecordsDirect({ ...filters, academic_year: currentAcademicYear ?? '', active_only: true });
    },
    enabled: !!schoolContext && !!entityId && !!currentAcademicYear,
  });

  if (loadingRecords || loadingSubstitutions) return <LoadingSpinner />;

  // Derive timeslots by deduplicating record.period entries, sorted by time_start
  const seen = new Set<number>();
  const timeslots: SchedulePeriod[] = [];
  for (const r of [...(records ?? [])].sort((a, b) =>
    (a.period?.time_start || '').localeCompare(b.period?.time_start || '')
  )) {
    if (r.period && !seen.has(r.period.period_number)) {
      seen.add(r.period.period_number);
      timeslots.push(r.period as SchedulePeriod);
    }
  }

  if (!records || timeslots.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No timetable records found for this selection.</Typography>
      </Box>
    );
  }

  // Derive days dynamically from records so Saturday/Sunday show when data exists
  const daysWithData = new Set(records.map(r => r.day_of_week as DayOfWeek));
  const DAYS = DAY_ORDER.filter(d => daysWithData.has(d));

  const findRecord = (day: DayOfWeek, periodNumber: number): TimetableRecordWithRelations | undefined => {
    return records?.find(
      r => r.day_of_week === day && r.period?.period_number === periodNumber
    );
  };

  const gridCols = `52px repeat(${DAYS.length}, minmax(100px, 1fr))`;

  // Check if teacher has no scheduled periods on the selected date (Requirement 17.8)
  const teacherAbsences = viewMode === 'teacher' && temporalDate ? getTeacherAbsences(entityId) : [];
  const teacherIsAbsentWithNoPeriods = viewMode === 'teacher' && temporalDate && isTeacherAbsent(entityId) && records.length === 0;

  return (
    <Box>
      {temporalDate && (
        <Box
          sx={{
            p: 1.5, mb: 2,
            backgroundColor: 'warning.lighter',
            border: 1, borderColor: 'warning.main', borderRadius: 1,
            display: 'flex', alignItems: 'center', gap: 1,
          }}
        >
          <HistoryIcon color="warning" fontSize="small" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
              Viewing timetable for {format(temporalDate, 'MMMM d, yyyy')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Substitute assignments are overlaid on the base timetable.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Requirement 17.8: Banner when absent teacher has no scheduled periods */}
      {teacherIsAbsentWithNoPeriods && (
        <Box
          sx={{
            p: 1.5, mb: 2,
            backgroundColor: 'info.lighter',
            border: 1, borderColor: 'info.main', borderRadius: 1,
            display: 'flex', alignItems: 'center', gap: 1,
          }}
        >
          <WarningIcon color="info" fontSize="small" />
          <Typography variant="body2" color="info.dark">
            Teacher is absent on this date but has no scheduled periods.
          </Typography>
        </Box>
      )}

      <Box
        ref={gridRef}
        role="grid"
        aria-label="Timetable grid"
        sx={{
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: gridCols,
          gridTemplateRows: `auto repeat(${timeslots.length}, auto)`,
          width: '100%',
        }}
      >
        {/* Header row */}
        {/* Time header corner */}
        <Box
          sx={{
            gridColumn: 1, gridRow: 1,
            borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'sticky', top: 0, zIndex: 4,
          }}
        >
          <Typography variant="caption" color="text.secondary">Time</Typography>
        </Box>

        {DAYS.map((day, di) => (
          <DayHeader
            key={day}
            sx={{
              gridColumn: 2 + di, gridRow: 1,
              borderRight: di < DAYS.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none',
              borderBottom: '1px solid', borderColor: 'divider',
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
              {DAY_LABELS[day]}
            </Typography>
          </DayHeader>
        ))}

        {/* Period rows */}
        {timeslots.map((timeslot, periodIndex) => (
          <>
            {/* Time label */}
            <TimeLabel
              key={`time-${timeslot.period_number}`}
              sx={{
                gridColumn: 1, gridRow: 2 + periodIndex,
                borderRight: '1px solid',
                borderBottom: periodIndex < timeslots.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.65rem' }}>
                {formatTime(timeslot.time_start)}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                {formatTime(timeslot.time_end)}
              </Typography>
            </TimeLabel>

            {/* Day cells */}
            {DAYS.map((day, dayIndex) => {
              const record = findRecord(day, timeslot.period_number);
              const isFocused = focusedCell?.dayIndex === dayIndex && focusedCell?.periodIndex === periodIndex;

              // Substitution overlay logic (Requirements 17.2, 17.3, 17.6, 17.7)
              let substitution = null;
              let isAbsent = false;
              let isCovering = false;

              if (temporalDate && record) {
                // Check for substitution based on view mode
                if (viewMode === 'class') {
                  // Class view: check if this period has a substitution
                  substitution = getSubstitution(day, record.period_id, undefined, record.class_section_id);
                } else if (viewMode === 'teacher') {
                  // Teacher view: check if this teacher is absent or covering
                  const absentSub = getSubstitution(day, record.period_id, entityId, undefined);
                  const coveringSub = isTeacherCovering(entityId, day, record.period_id);
                  
                  if (absentSub) {
                    substitution = absentSub;
                    isAbsent = true;
                  } else if (coveringSub) {
                    substitution = coveringSub;
                    isCovering = true;
                  }
                }
              }

              return (
                <PeriodCell
                  key={`${day}-${timeslot.period_number}`}
                  // TimetableGrid is view-only, never show the outer focus ring
                  isFocused={false}
                  tabIndex={isFocused ? 0 : -1}
                  role="gridcell"
                  aria-label={record ? `${record.subject?.subject_name} with ${record.teacher?.name}` : 'Empty slot'}
                  sx={{
                    gridColumn: 2 + dayIndex, gridRow: 2 + periodIndex,
                    borderRight: dayIndex < DAYS.length - 1 ? '1px solid' : 'none',
                    borderBottom: periodIndex < timeslots.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    p: 0.5,
                  }}
                  onClick={() => {
                    setFocusedCell({ dayIndex, periodIndex });
                    const period = timeslots.find(p => p.period_number === timeslot.period_number);
                    if (period) selectPeriod(day, period);
                  }}
                >
                  {timeslot.is_break ? (
                    <BreakPeriodCard period={timeslot} />
                  ) : record ? (
                    <PeriodCard
                      record={record}
                      viewMode={viewMode}
                      isSelected={isFocused}
                      onEdit={(r) => {
                        setRecordForStructuralChange(r);
                        setStructuralChangeDialogOpen(true);
                      }}
                      substitution={substitution}
                      isAbsent={isAbsent}
                      isCovering={isCovering}
                      isOverlayMode={!!temporalDate && !!substitution}
                    />
                  ) : (
                    <EmptySlotCell editMode={false} />
                  )}
                </PeriodCell>
              );
            })}
          </>
        ))}
      </Box>

      <StructuralChangeDialog
        open={structuralChangeDialogOpen}
        onClose={() => { setStructuralChangeDialogOpen(false); setRecordForStructuralChange(null); }}
        record={recordForStructuralChange}
      />
    </Box>
  );
}
